import { Box, Button, Flex, Icon, Text } from '@chakra-ui/react'
import {
  Pause,
  Phone,
  PhoneMissed,
  PhoneOff,
  SquareParking,
} from 'lucide-react'
import React, { ReactNode, useCallback, useId, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import { CircleBadge } from '~/components/shared/CircleBadge'
import { CircleBadgeVariant } from '~/components/shared/componentStyles/CircleBadge.style'
import {
  Menu,
  MenuPlacement,
  MenuPositioningWrapper,
} from '~/components/shared/Dropdown/Menu'
import {
  MangoButton,
  MangoButtonProps,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { castRef } from '~/utils/refUtils'
import { PresenceState } from '~/utils/xml'

import { useCallIconStyle } from './componentStyles/CallIcon.style'
import {
  IncomingCallButtonVariants,
  useIncomingCallButtonStyles,
} from './componentStyles/IncomingCallButton.style'
import {
  ParkCallMenuStyleValues,
  useParkCallMenuStyle,
} from './componentStyles/ParkCallMenu.style'
import {
  ParkCallMenuItemVariants,
  useParkCallMenuItemStyle,
} from './componentStyles/ParkCallMenuItem.style'

interface CallIconProps {
  icon: React.ComponentType<any>
  variant: IncomingCallButtonVariants
}
const CallIcon = ({ icon, variant }: CallIconProps) => {
  const styles = useCallIconStyle({ variant })
  const IconComp = icon
  return (
    <Flex css={styles.container}>
      <Icon css={styles.icon}>
        <IconComp size="100%" />
      </Icon>
    </Flex>
  )
}

const RejectCall = () => {
  return (
    <CallIcon icon={PhoneMissed} variant={IncomingCallButtonVariants.Reject} />
  )
}

const AcceptCall = () => {
  return <CallIcon icon={Phone} variant={IncomingCallButtonVariants.Accept} />
}

const AcceptCallWithOverlap = () => {
  return (
    <CallIcon
      icon={Phone}
      variant={IncomingCallButtonVariants.AcceptWithOverlap}
    />
  )
}

const EndAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={PhoneOff}
        variant={IncomingCallButtonVariants.EndAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

const HoldAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={Pause}
        variant={IncomingCallButtonVariants.HoldAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

const ParkAcceptCall = () => {
  return (
    <Flex>
      <CallIcon
        icon={SquareParking}
        variant={IncomingCallButtonVariants.ParkAndAccept}
      />
      <AcceptCallWithOverlap />
    </Flex>
  )
}

// Placeholder until we have a good disabled state
const IncomingCallButtonDummy = () => {
  return <Box flexShrink="0" boxSize="8.75rem" />
}

type IncomingCallButtonProps = {
  variant: IncomingCallButtonVariants
  children: ReactNode
  onClick: () => void
  isActive?: boolean
  ref?: React.Ref<HTMLButtonElement>
  allowKeyboardActivation?: boolean
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
  'aria-controls'?: string
}
export const IncomingCallButton = ({
  variant,
  children,
  onClick,
  isActive,
  ref,
  allowKeyboardActivation = true,
  ...ariaProps
}: IncomingCallButtonProps) => {
  let ButtonIcon: () => React.JSX.Element

  switch (variant) {
    case IncomingCallButtonVariants.Reject:
      ButtonIcon = RejectCall
      break
    case IncomingCallButtonVariants.Accept:
      ButtonIcon = AcceptCall
      break
    case IncomingCallButtonVariants.EndAndAccept:
      ButtonIcon = EndAcceptCall
      break
    case IncomingCallButtonVariants.HoldAndAccept:
      ButtonIcon = HoldAcceptCall
      break
    case IncomingCallButtonVariants.ParkAndAccept:
      ButtonIcon = ParkAcceptCall
      break
    case IncomingCallButtonVariants.AcceptWithOverlap:
      ButtonIcon = AcceptCallWithOverlap
  }

  const styles = useIncomingCallButtonStyles({ variant })

  // Handle keyboard events - only allow activation if explicitly enabled
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!allowKeyboardActivation && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
    },
    [allowKeyboardActivation],
  )

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const buttonProps = {
    flexDirection: styles.button?.flexDirection,
    justifyContent: styles.button?.justifyContent,
    alignItems: styles.button?.alignItems,
  }

  return (
    <Button
      ref={ref}
      css={styles.button}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-active={isActive ? '' : undefined}
      {...ariaProps}
      {...buttonProps}
    >
      <ButtonIcon />
      <Text css={styles.text}>{children}</Text>
    </Button>
  )
}

interface ParkCallMenuItemProps {
  parkingLot: TrackedParkingLot
}
const ParkCallMenuItem = ({ parkingLot }: ParkCallMenuItemProps) => {
  let circleBadgeVariant: CircleBadgeVariant
  let itemVariant: ParkCallMenuItemVariants
  switch (parkingLot.presence) {
    case PresenceState.Available:
      circleBadgeVariant = CircleBadgeVariant.Green
      itemVariant = ParkCallMenuItemVariants.Available
      break
    case PresenceState.Unavailable:
      circleBadgeVariant = CircleBadgeVariant.Red
      itemVariant = ParkCallMenuItemVariants.Unavailable
      break
    default:
      circleBadgeVariant = CircleBadgeVariant.Gray
      itemVariant = ParkCallMenuItemVariants.Available
      break
  }

  const styles = useParkCallMenuItemStyle({ variant: itemVariant })
  return (
    <Flex css={styles.container}>
      <CircleBadge variant={circleBadgeVariant} />
      <Text css={styles.text}>{parkingLot.label}</Text>
    </Flex>
  )
}

interface ParkCallMenuProps {
  id: string
  anchorRef?: React.RefObject<HTMLDivElement>
  parkingLots: TrackedParkingLot[]
  onClickParkingLot: (parkingLot: TrackedParkingLot) => void
  onClose: () => void
  isInPopup?: boolean
}
const ParkCallMenu = function ParkCallMenu({
  id,
  anchorRef,
  parkingLots,
  onClickParkingLot,
  onClose,
  isInPopup = false,
}: ParkCallMenuProps) {
  const { t } = useTranslation()
  const styles = useParkCallMenuStyle()
  const headerId = useId()

  const menuItems = parkingLots.map((parkingLot) => ({
    value: parkingLot.id,
    clickDisabled: parkingLot.presence === PresenceState.Unavailable,
    onSelect: () => onClickParkingLot(parkingLot),
    content: <ParkCallMenuItem key={parkingLot.id} parkingLot={parkingLot} />,
  }))

  return (
    <Menu
      anchorRef={anchorRef}
      items={menuItems}
      header={
        <Flex css={{ ...styles.headerContainer, paddingY: '0.25rem' }}>
          <Text id={headerId} css={styles.headerText}>
            {t('webPhone.selectParkingSpot', 'Select a parking spot')}
          </Text>
        </Flex>
      }
      keyboard={{ id, onClose, labelledBy: headerId }}
      options={{
        maxHeight: isInPopup ? '70vh' : 'inherit',
        width: isInPopup
          ? ParkCallMenuStyleValues.containerWidthPopup
          : ParkCallMenuStyleValues.containerWidth,
        placement: MenuPlacement.RightTop,
      }}
    />
  )
}

type ParkCallButtonProps = {
  parkingLots: TrackedParkingLot[]
  onClickParkingLot: (parkingLot: TrackedParkingLot) => void
  isInPopup?: boolean
  mangoButtonProps?: MangoButtonProps
} & Omit<IncomingCallButtonProps, 'ref'>
export const ParkCallButton = ({
  parkingLots,
  onClickParkingLot,
  isInPopup = false,
  mangoButtonProps = {},
  ...rest
}: ParkCallButtonProps) => {
  const { t } = useTranslation()
  const [menuOpenState, setMenuOpenState] = useState(false)
  const boxRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const menuId = useId()

  useOutsideClick({
    ref: castRef(boxRef),
    handler: () => {
      setMenuOpenState(false)
    },
  })

  const handleButtonClick = () => {
    setMenuOpenState((prev) => !prev)
  }

  // Tab must unmount the menu before the browser moves focus on from the
  // trigger, or focus lands on a menu item that is about to disappear.
  const handleMenuKeyboardClose = () => {
    flushSync(() => setMenuOpenState(false))
    triggerRef.current?.focus()
  }

  const menuTriggerProps = {
    'aria-haspopup': 'menu',
    'aria-expanded': menuOpenState,
    'aria-controls': menuOpenState ? menuId : undefined,
  } as const

  if (parkingLots.length === 0) {
    // UX: Could replace with a 'disabled' style button instead
    return <IncomingCallButtonDummy />
  }

  return (
    <>
      {isInPopup && (
        <Flex
          ref={boxRef}
          direction="column"
          flex="1 0 auto"
          position="relative"
        >
          <Tooltip content={t('popupCallButtons.tooltip.parkAndAccept')}>
            <MangoButton
              ref={triggerRef}
              aria-label={t('popupCallButtons.tooltip.parkAndAccept')}
              {...menuTriggerProps}
              {...mangoButtonProps}
              flex={'1 0 auto'}
              leftIcon={SquareParking}
              leftIconProps={{
                boxSize: 4,
              }}
              variant={MangoButtonVariant.Success_300}
              onClick={handleButtonClick}
            />
          </Tooltip>
          {menuOpenState && (
            <Box
              position="absolute"
              marginTop={1}
              top="100%"
              right="0"
              zIndex={1000}
            >
              <ParkCallMenu
                id={menuId}
                isInPopup={isInPopup}
                parkingLots={parkingLots}
                onClickParkingLot={onClickParkingLot}
                onClose={handleMenuKeyboardClose}
              />
            </Box>
          )}
        </Flex>
      )}

      {!isInPopup && (
        <MenuPositioningWrapper ref={boxRef}>
          <IncomingCallButton
            {...rest}
            {...menuTriggerProps}
            ref={triggerRef}
            onClick={handleButtonClick}
            isActive={menuOpenState}
          />
          {menuOpenState && (
            <ParkCallMenu
              id={menuId}
              parkingLots={parkingLots}
              onClickParkingLot={onClickParkingLot}
              onClose={handleMenuKeyboardClose}
            />
          )}
        </MenuPositioningWrapper>
      )}
    </>
  )
}
